import React, { useMemo, useRef, useState } from "react";
import { useDrag } from "react-dnd";
import { DateTime } from "luxon";


import {
    FaseBarDragItem,
    FASE_BAR_TYPE,
    FASE_RESIZE_LEFT_TYPE,
    FASE_RESIZE_RIGHT_TYPE,
} from "./schedulazioneGanttTypes";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import LinearProgress from "@mui/material/LinearProgress";
import { useTheme } from "@mui/material/styles";
import { yellow } from "@mui/material/colors";
import {FaseFrazione} from "../frazioniOdPSlice";
import Button from "@mui/material/Button";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Tooltip from "@mui/material/Tooltip";
import { isTempoNonAttendibile } from "../../../common/claire/tempoUtils";
import { SENZA_OPERATORE_ID } from "./GanttHeader";

// ============================================
// CONSTANTS
// ============================================

export const ALTEZZA_FASE = 28;
export const PADDING_FASE = 2;
export const ALTEZZA_LANE_FASE = ALTEZZA_FASE + PADDING_FASE;

// ============================================
// COLORI FASE PER STATO
// ============================================

const getColoriStato = (
    stato?: number
): { bg: string; border: string } => {
    switch (stato) {
        case 0: // Da pianificare
            return { bg: "#90a4ae", border: "#607d8b" };
        case 1: // Pianificato
            return { bg: "#42a5f5", border: "#1976d2" };
        case 2: // Lanciato
            return { bg: "#66bb6a", border: "#388e3c" };
        case 3: // In avanzamento
            return { bg: "#ffa726", border: "#f57c00" };
        case 4: // Terminato
            return { bg: "#a5d6a7", border: "#66bb6a" };
        default:
            return { bg: "#bdbdbd", border: "#9e9e9e" };
    }
};

// ============================================
// SUB-COMPONENTS
// ============================================

interface ResizeHandleProps {
    item: FaseBarDragItem;
    edge: "left" | "right";
}

const ResizeHandle = ({ item, edge }: ResizeHandleProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const type =
        edge === "left" ? FASE_RESIZE_LEFT_TYPE : FASE_RESIZE_RIGHT_TYPE;

    const [{ isDragging }, drag] = useDrag(
        () => ({
            type,
            item: { ...item, type, resizeEdge: edge },
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [type, item, edge]
    );

    drag(ref);

    return (
        <Box
            ref={ref}
            sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                width: 8,
                [edge]: 0,
                cursor: "ew-resize",
                backgroundColor: isDragging
                    ? "rgba(255,255,255,0.5)"
                    : "transparent",
                "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.3)",
                },
                zIndex: 1,
            }}
        />
    );
};

// ============================================
// INTERFACES
// ============================================

interface GanttFaseBarProps {
    fase: FaseFrazione;
    frazioneId: number;
    /** Data inizio della frazione contenitore (per posizionamento relativo) */
    dataInizioFrazione: DateTime;
    /** Data fine della frazione contenitore */
    dataFineFrazione: DateTime;
    lane: number;
    onClick?: (faseFrazioneId: number) => void;
    onFaseDelete: (faseFrazioneToDelete: FaseFrazione) => void;
    onFaseEdit: (faseFrazione: FaseFrazione) => void;
    repartiSelezionati?: number[];
    operatoriSelezionati?: number[];
}

// ============================================
// COMPONENT
// ============================================

const GanttFaseBar = ({
                          fase,
                          frazioneId,
                          dataInizioFrazione,
                          dataFineFrazione,
                          lane,
                          onClick,
                          onFaseDelete,
                          onFaseEdit,
                          repartiSelezionati,
                          operatoriSelezionati,
                      }: GanttFaseBarProps) => {
    const theme = useTheme();
    const ref = useRef<HTMLDivElement>(null);

    const colori = getColoriStato(fase.stato);

    // Evidenziazione per filtro reparti e/o operatori
    const filtroRepartiAttivo = repartiSelezionati && repartiSelezionati.length > 0;
    const filtroOperatoriAttivo = operatoriSelezionati && operatoriSelezionati.length > 0;
    const filtroAttivo = filtroRepartiAttivo || filtroOperatoriAttivo;
    const faseEvidenziataReparto = filtroRepartiAttivo && fase.reparto && repartiSelezionati.includes(fase.reparto);
    const faseEvidenziataOperatore = filtroOperatoriAttivo && (
        (fase.operatore && operatoriSelezionati.includes(fase.operatore))
        || (!fase.operatore && operatoriSelezionati.includes(SENZA_OPERATORE_ID))
    );
    // Quando entrambi i filtri sono attivi, l'evidenziazione è l'intersezione (AND);
    // diversamente basta che la fase soddisfi il filtro attivo.
    const faseEvidenziata = filtroRepartiAttivo && filtroOperatoriAttivo
        ? !!(faseEvidenziataReparto && faseEvidenziataOperatore)
        : !!(faseEvidenziataReparto || faseEvidenziataOperatore);

    // Drag item
    const dragItem: FaseBarDragItem = useMemo(
        () => ({
            type: FASE_BAR_TYPE,
            faseFrazioneId: fase.id!,
            frazioneId,
            dataDa: fase.data_inizio_pianificata as string,
            dataA: fase.data_fine_pianificata as string,
        }),
        [fase.id, frazioneId, fase.data_inizio_pianificata, fase.data_fine_pianificata]
    );

    // Calcola posizione e larghezza RELATIVA alla frazione (0%-100%)
    // Le date della frazione possono essere clampate al range visibile del Gantt
    const { left, width, isVisible, clippedLeft: faseClippedLeft, clippedRight: faseClippedRight } = useMemo(() => {
        if (!fase.data_inizio_pianificata || !fase.data_fine_pianificata) {
            return { left: "0%", width: "0%", isVisible: false, clippedLeft: false, clippedRight: false };
        }

        const faseDataDaOriginale = DateTime.fromISO(fase.data_inizio_pianificata);
        const faseDataAOriginale = DateTime.fromISO(fase.data_fine_pianificata);

        // Fase completamente fuori dal range visibile della frazione
        if (faseDataAOriginale < dataInizioFrazione || faseDataDaOriginale > dataFineFrazione) {
            return { left: "0%", width: "0%", isVisible: false, clippedLeft: false, clippedRight: false };
        }

        // Clamp fase alle date effettive della frazione
        const isClippedLeft = faseDataDaOriginale < dataInizioFrazione;
        const isClippedRight = faseDataAOriginale > dataFineFrazione;
        const faseDataDa = isClippedLeft ? dataInizioFrazione : faseDataDaOriginale;
        const faseDataA = isClippedRight ? dataFineFrazione : faseDataAOriginale;

        // Giorni lavorativi totali della frazione (denominatore)
        let giorniFrazione = 0;
        let giorno = dataInizioFrazione;
        while (giorno <= dataFineFrazione) {
            if (giorno.weekday <= 5) {
                giorniFrazione++;
            }
            giorno = giorno.plus({ days: 1 });
        }

        if (giorniFrazione === 0) {
            return { left: "0%", width: "0%", isVisible: false, clippedLeft: false, clippedRight: false };
        }

        // Giorni lavorativi dall'inizio della frazione
        let giorniDaInizio = 0;
        giorno = dataInizioFrazione;
        while (giorno < faseDataDa) {
            if (giorno.weekday <= 5) {
                giorniDaInizio++;
            }
            giorno = giorno.plus({ days: 1 });
        }

        // Durata fase in giorni lavorativi
        let durataGiorni = 0;
        giorno = faseDataDa;
        while (giorno <= faseDataA) {
            if (giorno.weekday <= 5) {
                durataGiorni++;
            }
            giorno = giorno.plus({ days: 1 });
        }

        const leftPercent = (giorniDaInizio / giorniFrazione) * 100;
        const widthPercent = (durataGiorni / giorniFrazione) * 100;

        return {
            left: `${leftPercent}%`,
            width: `${widthPercent}%`,
            isVisible: true,
            clippedLeft: isClippedLeft,
            clippedRight: isClippedRight,
        };
    }, [
        fase.data_inizio_pianificata,
        fase.data_fine_pianificata,
        dataInizioFrazione,
        dataFineFrazione,
    ]);

    // Drag per spostamento intera barra
    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: FASE_BAR_TYPE,
            item: dragItem,
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [dragItem]
    );

    drag(ref);

    // Hooks devono stare PRIMA di ogni early return
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
        if (onClick && fase.id) {
            onClick(fase.id);
        }
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    if (!isVisible) {
        return null;
    }

    // Tooltip
    const tooltipContent = (
        <Box>
            <Typography variant="body2" fontWeight="bold">
                {fase.fase_legacy_display || fase.codice_lavorazione || "-"}
            </Typography>
            <Typography variant="caption" display="block">
                Reparto: {fase.reparto_display || "-"}
            </Typography>
            <Typography variant="caption" display="block">
                Operatore: {fase.operatore_display || "-"}
            </Typography>
            <Typography variant="caption" display="block">
                Qtà: {Intl.NumberFormat("it-IT", {maximumFractionDigits: 0, minimumFractionDigits: 0}).format(fase.quantita ?? 0)}
            </Typography>
            <Typography variant="caption" display="block">
                {`Ore schedulate: ${Intl.NumberFormat("it-IT", {
                style: "decimal",
                useGrouping: true,
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
            }).format(fase.ore_previste ?? 0)} h` }
                {isTempoNonAttendibile(fase.ore_previste) && (
                    <span style={{ color: "#ffa726", marginLeft: 4 }}>⚠ Tempo non attendibile</span>
                )}
            </Typography>
            {fase.data_inizio_pianificata && fase.data_fine_pianificata && (
                <Typography variant="caption" display="block">
                    {DateTime.fromISO(fase.data_inizio_pianificata).toFormat("dd/MM")} → {DateTime.fromISO(fase.data_fine_pianificata).toFormat("dd/MM")}
                </Typography>
            )}
            {fase.is_bloccata && (
                <Typography variant="caption" display="block" color="error">
                    ⚠ Bloccata (ostacoli attivi)
                </Typography>
            )}
            <Button variant="contained" sx={{mt:1}} color='primary' size="small" onClick={()=>onFaseEdit(fase)}>Modifica Fase</Button>
            <Button variant="contained" sx={{mt:1, ml:1}} color='warning' size="small" onClick={()=>onFaseDelete(fase)}>Elimina Fase</Button>
        </Box>
    );

    return (
        <>
            <Box
                ref={ref}
                onClick={handleClick}
                sx={{
                    position: "absolute",
                    top: PADDING_FASE + lane * ALTEZZA_LANE_FASE,
                    left,
                    width,
                    height: ALTEZZA_FASE,
                    backgroundColor: isTempoNonAttendibile(fase.ore_previste)?'#c87acb':faseEvidenziata ? yellow[100] : colori.bg,
                    border: faseEvidenziata
                        ? `2.5px solid ${yellow[500]}`
                        : `1.5px solid ${colori.border}`,
                    borderRadius: 0.5,
                    // Lati tagliati: rimuovi bordo e border-radius
                    ...(faseClippedLeft && {
                        borderLeft: "none",
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                    }),
                    ...(faseClippedRight && {
                        borderRight: "none",
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                    }),
                    cursor: "grab",
                    opacity: isDragging ? 0.4 : filtroAttivo && !faseEvidenziata ? 0.3 : 1,
                    pointerEvents: "auto",
                    display: "flex",
                    alignItems: "center",
                    px: 0.5,
                    overflow: "hidden",
                    transition: "box-shadow 0.2s, opacity 0.2s, border 0.2s",
                    boxShadow: faseEvidenziata ? `0 0 6px 2px ${yellow[200]}` : undefined,
                    "&:hover": {
                        boxShadow: theme.shadows[3],
                    },
                    ...(fase.is_bloccata && {
                        borderStyle: "dashed",
                        borderColor: theme.palette.error.main,
                    }),
                }}
            >
                {/* Handle resize sinistro (nascosto se il lato è tagliato) */}
                {!faseClippedLeft && <ResizeHandle item={dragItem} edge="left" />}

                {/* Contenuto */}
                <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                        overflow: "hidden",
                        flexGrow: 1,
                        alignItems: "center",
                        mx: 1,

                    }}
                >
                    <Typography
                        variant="caption"
                        noWrap
                        sx={{
                            color: faseEvidenziata ? theme.palette.text.primary : "#fff",
                            fontWeight: "bold",
                            fontSize: "0.65rem",
                        }}
                    >
                        {fase.codice_lavorazione}
                    </Typography>
                    {/*<Typography*/}
                    {/*    variant="caption"*/}
                    {/*    noWrap*/}
                    {/*    sx={{*/}
                    {/*        color: faseEvidenziata ? "rgba(0,0,0,0.7)" : "rgba(255,255,255,0.85)",*/}
                    {/*        fontSize: "0.6rem",*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    {fase.reparto_display}*/}
                    {/*</Typography>*/}
                    {isTempoNonAttendibile(fase.ore_previste) && (
                        <Tooltip title="Tempo non attendibile">
                            <WarningAmberIcon sx={{ fontSize: 18, color: "#e5bd0f", ml: 0.25, filter: "drop-shadow(0 0 1px rgba(0,0,0,0.5))" }} />
                        </Tooltip>
                    )}
                </Stack>

                {/* Mini barra avanzamento */}
                {(Number(fase.ore_lavorate) || 0) > 0 && (
                    <LinearProgress
                        variant="determinate"
                        value={(Number(fase.ore_lavorate || 0) / Number(fase.ore_previste || 1)) * 100}
                        sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            backgroundColor: faseEvidenziata ? "rgba(0,0,0,0.15)" : "rgba(0,0,0,0.2)",
                            "& .MuiLinearProgress-bar": {
                                backgroundColor: faseEvidenziata ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.7)",
                            },
                        }}
                    />
                )}

                {/* Handle resize destro (nascosto se il lato è tagliato) */}
                {!faseClippedRight && <ResizeHandle item={dragItem} edge="right" />}
            </Box>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: "rgba(97,97,97,0.92)",
                            color: "#fff",
                            borderRadius: "4px",
                            mt: "10px",
                            overflow: "visible",
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: "50%",
                                transform: "translate(-50%, -100%)",
                                borderWidth: "0 8px 8px 8px",
                                borderStyle: "solid",
                                borderColor: "transparent transparent rgba(97,97,97,0.92) transparent",
                            },
                        },
                    },
                }}
            >
                <Box sx={{ p: 1.5 }}>
                    {tooltipContent}
                </Box>
            </Popover>
        </>
    );
};

export default GanttFaseBar;